import { useEffect, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Plus, Trash2, LogOut, Sparkles, ImagePlus, Send, Mail, MapPin, Bell, Moon, CloudSun, Activity, Settings } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";

const TABS_BASE = ["Hunts", "Gear Locker", "Journal", "Saved"];
const VERDICT_TONE = { keep: "text-[#2f7d4f]", keep_with_conditions: "text-saddle", upgrade: "text-blaze" };

function resizeImage(file, max = 900) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const c = document.createElement("canvas");
        c.width = img.width * scale;
        c.height = img.height * scale;
        c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
        resolve(c.toDataURL("image/jpeg", 0.75));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

function Thread({ messages }) {
  if (!messages?.length) return <p className="text-xs text-charcoal/40 mt-2">No messages yet.</p>;
  return (
    <div className="space-y-2 mt-2" data-testid="thread-messages">
      {messages.map((m, i) => (
        <div key={i} className={`p-2.5 text-sm max-w-[88%] ${m.sender === "provider" ? "bg-forest text-bone" : "bg-bone text-charcoal ml-auto"}`}>
          <span className="hr-overline text-[8px] opacity-70 block mb-0.5">{m.sender === "provider" ? "Provider" : (m.name || "Hunter")}</span>
          {m.text}
        </div>
      ))}
    </div>
  );
}

export function ProfileDrawer() {
  const { profileOpen, setProfileOpen, setBuildHuntOpen, openAI, shareToFeed } = useUI();
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("Hunts");
  const [hunts, setHunts] = useState([]);
  const [gear, setGear] = useState([]);
  const [saves, setSaves] = useState([]);
  const [compat, setCompat] = useState([]);
  const [newGear, setNewGear] = useState({ category: "Packs", product_name: "", brand: "" });
  const [journals, setJournals] = useState([]);
  const [newJournal, setNewJournal] = useState({ title: "", body: "", visibility: "private", hunt_id: "", general_location: "", entry_date: "", photos: [] });
  const [inquiries, setInquiries] = useState([]);
  const [replyDraft, setReplyDraft] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [myInquiries, setMyInquiries] = useState([]);
  const [threadDraft, setThreadDraft] = useState({});
  const [digestBusy, setDigestBusy] = useState(false);
  const [digestPrefs, setDigestPrefs] = useState({ digest_frequency: "weekly", digest_day: "mon" });
  const [conditions, setConditions] = useState({});

  const isAdmin = (user?.roles || []).includes("admin");
  const TABS = isAdmin ? [...TABS_BASE, "Messages", "Inbox"] : [...TABS_BASE, "Messages"];

  const refresh = useCallback(async () => {
    try {
      const [h, g, s, j, al, mi, bundle] = await Promise.all([api.hunts(), api.gear(), api.saves(), api.journal(), api.alerts(), api.myInquiries(), api.bundle()]);
      setHunts(h); setGear(g); setSaves(s); setJournals(j); setAlerts(al); setMyInquiries(mi);
      if (bundle?.profile) setDigestPrefs({ digest_frequency: bundle.profile.digest_frequency || "weekly", digest_day: bundle.profile.digest_day || "mon" });
      if (h.length) setCompat(await api.gearCompatibility(h[0].id).catch(() => []));
      Promise.all(h.map((hunt) => api.huntConditions(hunt.id).then((c) => [hunt.id, c]).catch(() => null)))
        .then((pairs) => setConditions(Object.fromEntries(pairs.filter(Boolean))));
      if ((user?.roles || []).includes("admin")) setInquiries(await api.adminInquiries().catch(() => []));
    } catch {}
  }, [user]);

  const saveDigestPrefs = async (patch) => {
    const next = { ...digestPrefs, ...patch };
    setDigestPrefs(next);
    try { await api.updateProfile(next); toast.success("Digest preferences saved"); } catch { toast.error("Could not save"); }
  };

  useEffect(() => { if (profileOpen) refresh(); }, [profileOpen, refresh]);

  const addGear = async () => {
    if (!newGear.product_name.trim()) return;
    try {
      await api.addGear(newGear);
      setNewGear({ category: "Packs", product_name: "", brand: "" });
      toast.success("Added to Gear Locker");
      refresh();
    } catch { toast.error("Could not add gear"); }
  };
  const removeGear = async (id) => { await api.deleteGear(id); toast.success("Removed"); refresh(); };

  const addJournal = async () => {
    if (!newJournal.title.trim()) return;
    try {
      await api.createJournal({ ...newJournal, hunt_id: newJournal.hunt_id || null });
      setNewJournal({ title: "", body: "", visibility: "private", hunt_id: "", general_location: "", entry_date: "", photos: [] });
      toast.success("Journal entry saved");
      refresh();
    } catch { toast.error("Could not save entry"); }
  };
  const removeJournal = async (id) => { await api.deleteJournal(id); toast.success("Deleted"); refresh(); };

  const sendDigest = async () => {
    setDigestBusy(true);
    try {
      const r = await api.alertDigest();
      toast.success(r.sent ? `Digest emailed — ${r.count} update(s)` : "Digest prepared (email not delivered for this address)");
    } catch { toast.error("Could not send digest"); }
    finally { setDigestBusy(false); }
  };

  const sendThreadMessage = async (id) => {
    const text = (threadDraft[id] || "").trim();
    if (!text) return;
    try {
      await api.inquiryMessage(id, text);
      setThreadDraft((d) => ({ ...d, [id]: "" }));
      refresh();
    } catch { toast.error("Could not send message"); }
  };

  const onPickPhotos = async (e) => {
    const files = [...e.target.files].slice(0, 3);
    const dataUrls = await Promise.all(files.map((f) => resizeImage(f)));
    setNewJournal((j) => ({ ...j, photos: [...(j.photos || []), ...dataUrls].slice(0, 3) }));
    e.target.value = "";
  };

  const sendReply = async (id) => {
    const text = (replyDraft[id] || "").trim();
    if (!text) return;
    try {
      await api.replyInquiry(id, text);
      setReplyDraft((d) => ({ ...d, [id]: "" }));
      toast.success("Reply sent");
      refresh();
    } catch { toast.error("Could not send reply"); }
  };

  const compatFor = (id) => compat.find((c) => c.locker_item_id === id);

  return (
    <AnimatePresence>
      {profileOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[65] bg-forest/40 backdrop-blur-sm" onClick={() => setProfileOpen(false)} />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 z-[66] h-full w-full max-w-md bg-paper/95 backdrop-blur-xl border-l border-saddle/20 flex flex-col"
            data-testid="profile-drawer"
          >
            <div className="flex items-start justify-between p-6 pb-4">
              <div>
                <span className="hr-overline text-saddle">My HomeRange</span>
                <h2 className="font-serif text-3xl text-forest mt-1">Welcome home.</h2>
              </div>
              <button onClick={() => setProfileOpen(false)} className="text-forest hover:text-blaze transition-colors" aria-label="Close" data-testid="profile-close"><X size={26} /></button>
            </div>

            <div className="mx-6 bg-bone p-4 flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-saddle text-white grid place-items-center font-accent font-bold">{(user?.display_name || "H").slice(0, 2).toUpperCase()}</div>
              <div>
                <strong className="text-forest">{user?.display_name}</strong>
                <p className="text-xs text-charcoal/55">{user?.email}</p>
              </div>
            </div>

            <div className="mx-6 mt-4 bg-bone border border-saddle/20 p-3" data-testid="digest-settings">
              <span className="hr-overline text-saddle text-[9px] flex items-center gap-1"><Settings size={11} /> Digest email</span>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <select value={digestPrefs.digest_frequency} onChange={(e) => saveDigestPrefs({ digest_frequency: e.target.value })} className="border border-saddle/25 px-2 py-1.5 text-sm outline-none bg-white" data-testid="digest-frequency">
                  <option value="off">Paused</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Every 2 weeks</option>
                  <option value="monthly">Monthly</option>
                </select>
                <select value={digestPrefs.digest_day} onChange={(e) => saveDigestPrefs({ digest_day: e.target.value })} disabled={digestPrefs.digest_frequency === "off"} className="border border-saddle/25 px-2 py-1.5 text-sm outline-none bg-white disabled:opacity-40" data-testid="digest-day">
                  {[["mon", "Mondays"], ["tue", "Tuesdays"], ["wed", "Wednesdays"], ["thu", "Thursdays"], ["fri", "Fridays"], ["sat", "Saturdays"], ["sun", "Sundays"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            </div>

            <div className="flex gap-1 px-6 mt-4 overflow-x-auto hr-scroll">
              {TABS.map((t) => (
                <button key={t} onClick={() => setTab(t)} className={`px-3 py-2 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${tab === t ? "border-blaze text-forest" : "border-transparent text-charcoal/50"}`} data-testid={`profile-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}>{t}</button>
              ))}
            </div>

            {alerts.length > 0 && (
              <div className="mx-6 mt-4 bg-blaze/10 border border-blaze/30 p-3" data-testid="alerts-banner">
                <div className="flex items-center justify-between">
                  <span className="hr-overline text-blaze-dark text-[9px] flex items-center gap-1"><Bell size={12} /> {alerts.length} alert{alerts.length > 1 ? "s" : ""}</span>
                  <button onClick={sendDigest} disabled={digestBusy} className="text-[11px] font-semibold text-forest hover:text-blaze transition-colors inline-flex items-center gap-1 disabled:opacity-50" data-testid="digest-btn"><Mail size={12} /> Email me this digest</button>
                </div>
                <div className="mt-2 space-y-1.5">
                  {alerts.map((a, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm" data-testid="alert-item">
                      <span className={`mt-0.5 w-1.5 h-1.5 rounded-full shrink-0 ${a.type === "price_low" ? "bg-[#2f7d4f]" : "bg-blaze"}`} />
                      <p className="text-charcoal/80"><strong className="text-forest">{a.title}</strong> — {a.detail}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex-1 overflow-auto hr-scroll px-6 py-4 space-y-3">
              {tab === "Hunts" && (
                <>
                  {hunts.map((h) => (
                    <div key={h.id} className="bg-white border border-saddle/20 p-4" data-testid="profile-hunt">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-serif text-xl text-forest">{h.title}</h3>
                          <p className="text-xs text-charcoal/55 mt-0.5">{h.species} • {h.jurisdiction} • {h.method}</p>
                        </div>
                        <button onClick={() => { setProfileOpen(false); openAI(`What's my readiness for ${h.title}?`); }} className="text-saddle hover:text-blaze transition-colors" aria-label="Ask AI"><Sparkles size={16} /></button>
                      </div>
                      {conditions[h.id] && (
                        <div className="mt-3 border-t border-saddle/15 pt-3" data-testid="hunt-conditions">
                          <div className="flex items-center justify-between">
                            <span className="hr-overline text-saddle text-[9px] flex items-center gap-1"><Activity size={11} /> Rut activity · {conditions[h.id].start_date}</span>
                            <span className={`text-xs font-bold ${conditions[h.id].rut.score >= 80 ? "text-blaze" : conditions[h.id].rut.score >= 60 ? "text-saddle" : "text-charcoal/60"}`}>{conditions[h.id].rut.label}</span>
                          </div>
                          <div className="mt-1.5 h-2 bg-bone overflow-hidden rounded-full">
                            <div className={`h-full ${conditions[h.id].rut.score >= 80 ? "bg-blaze" : "bg-saddle"}`} style={{ width: `${conditions[h.id].rut.score}%` }} data-testid="rut-bar" />
                          </div>
                          <p className="text-[11px] text-charcoal/55 mt-1.5">{conditions[h.id].rut.note}</p>
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            <span className="text-[10px] bg-forest text-bone px-2 py-1 inline-flex items-center gap-1"><Moon size={10} /> {conditions[h.id].start_moon.phase} · {conditions[h.id].start_moon.illumination}%</span>
                            <span className="text-[10px] bg-[#e6efe9] text-[#234b38] px-2 py-1 inline-flex items-center gap-1"><CloudSun size={10} /> {conditions[h.id].weather_source === "observed" ? "Observed" : "Typical"}: {conditions[h.id].weather}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  <button onClick={() => { setProfileOpen(false); setBuildHuntOpen(true); }} className="w-full border border-dashed border-saddle/40 py-3 text-saddle font-semibold hover:bg-bone transition-colors flex items-center justify-center gap-1.5" data-testid="profile-add-hunt"><Plus size={16} /> Build a new hunt</button>
                </>
              )}

              {tab === "Gear Locker" && (
                <>
                  <div className="bg-white border border-saddle/20 p-3 grid grid-cols-[1fr_1fr] gap-2">
                    <select value={newGear.category} onChange={(e) => setNewGear({ ...newGear, category: e.target.value })} className="border border-saddle/25 px-2 py-2 text-sm outline-none col-span-2" data-testid="gear-add-category">
                      {["Packs", "Boots", "Clothing", "Optics", "Camp", "Other"].map((c) => <option key={c}>{c}</option>)}
                    </select>
                    <input value={newGear.brand} onChange={(e) => setNewGear({ ...newGear, brand: e.target.value })} placeholder="Brand" className="border border-saddle/25 px-2 py-2 text-sm outline-none min-w-0" data-testid="gear-add-brand" />
                    <input value={newGear.product_name} onChange={(e) => setNewGear({ ...newGear, product_name: e.target.value })} placeholder="Item name" className="border border-saddle/25 px-2 py-2 text-sm outline-none min-w-0" data-testid="gear-add-name" />
                    <button onClick={addGear} className="col-span-2 bg-forest text-bone py-2 text-sm font-semibold hover:bg-forest-2 transition-colors" data-testid="gear-add-submit">Add to locker</button>
                  </div>
                  {gear.map((g) => {
                    const c = compatFor(g.id);
                    return (
                      <div key={g.id} className="bg-white border border-saddle/20 p-4 flex justify-between items-start" data-testid="profile-gear-item">
                        <div>
                          <span className="hr-overline text-saddle text-[9px]">{g.category}</span>
                          <h4 className="text-forest font-semibold">{g.product_name}</h4>
                          <p className="text-xs text-charcoal/50">{g.brand}</p>
                          {c && <p className={`text-xs mt-1 font-semibold ${VERDICT_TONE[c.verdict] || "text-charcoal"}`}>{c.verdict.replace(/_/g, " ")} · {c.score}/100</p>}
                        </div>
                        <button onClick={() => removeGear(g.id)} className="text-charcoal/40 hover:text-destructive transition-colors" aria-label="Remove" data-testid="gear-remove"><Trash2 size={16} /></button>
                      </div>
                    );
                  })}
                </>
              )}

              {tab === "Journal" && (
                <>
                  <div className="bg-white border border-saddle/20 p-3 space-y-2">
                    <input value={newJournal.title} onChange={(e) => setNewJournal({ ...newJournal, title: e.target.value })} placeholder="Entry title" className="w-full border border-saddle/25 px-2 py-2 text-sm outline-none" data-testid="journal-title" />
                    <textarea value={newJournal.body} onChange={(e) => setNewJournal({ ...newJournal, body: e.target.value })} placeholder="What happened in the field…" rows={3} className="w-full border border-saddle/25 px-2 py-2 text-sm outline-none resize-none" data-testid="journal-body" />
                    <div className="grid grid-cols-2 gap-2">
                      <select value={newJournal.hunt_id} onChange={(e) => setNewJournal({ ...newJournal, hunt_id: e.target.value })} className="border border-saddle/25 px-2 py-2 text-sm outline-none min-w-0" data-testid="journal-hunt">
                        <option value="">No hunt</option>
                        {hunts.map((h) => <option key={h.id} value={h.id}>{h.title}</option>)}
                      </select>
                      <select value={newJournal.visibility} onChange={(e) => setNewJournal({ ...newJournal, visibility: e.target.value })} className="border border-saddle/25 px-2 py-2 text-sm outline-none min-w-0" data-testid="journal-visibility">
                        <option value="private">Private</option>
                        <option value="public">Published</option>
                      </select>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-saddle cursor-pointer hover:text-blaze transition-colors w-fit" data-testid="journal-photo-label">
                      <ImagePlus size={16} /> Add field photos
                      <input type="file" accept="image/*" multiple className="hidden" onChange={onPickPhotos} data-testid="journal-photo-input" />
                    </label>
                    <div>
                      <input value={newJournal.general_location} onChange={(e) => setNewJournal({ ...newJournal, general_location: e.target.value })} placeholder="General location (e.g. Bitterroot Mtns, MT)" className="w-full border border-saddle/25 px-2 py-2 text-sm outline-none" data-testid="journal-location" />
                      <p className="text-[11px] text-charcoal/45 mt-1 flex items-center gap-1"><MapPin size={11} /> Only a general area is saved — your exact honey hole stays private.</p>
                    </div>
                    <label className="block">
                      <span className="hr-overline text-saddle text-[9px]">Entry date (auto-logs weather & moon)</span>
                      <input type="date" value={newJournal.entry_date} onChange={(e) => setNewJournal({ ...newJournal, entry_date: e.target.value })} className="w-full border border-saddle/25 px-2 py-2 text-sm outline-none mt-1" data-testid="journal-date" />
                    </label>
                    {newJournal.photos?.length > 0 && (
                      <div className="flex gap-2">
                        {newJournal.photos.map((p, i) => (
                          <div key={i} className="relative">
                            <img src={p} alt="" className="w-14 h-14 object-cover" />
                            <button onClick={() => setNewJournal((j) => ({ ...j, photos: j.photos.filter((_, idx) => idx !== i) }))} className="absolute -top-1.5 -right-1.5 bg-charcoal text-white rounded-full w-4 h-4 grid place-items-center text-[10px]">×</button>
                          </div>
                        ))}
                      </div>
                    )}
                    <button onClick={addJournal} className="w-full bg-forest text-bone py-2 text-sm font-semibold hover:bg-forest-2 transition-colors" data-testid="journal-save">Save entry</button>
                  </div>
                  {journals.map((j) => (
                    <div key={j.id} className="bg-white border border-saddle/20 p-4" data-testid="profile-journal-item">
                      <div className="flex justify-between items-start">
                        <div>
                          <span className={`hr-overline text-[9px] ${j.visibility === "public" ? "text-[#2f7d4f]" : "text-saddle"}`}>{j.visibility === "public" ? "Published" : "Private"}</span>
                          <h4 className="font-serif text-lg text-forest">{j.title}</h4>
                          {j.body && <p className="text-xs text-charcoal/60 mt-1 line-clamp-3">{j.body}</p>}
                          {j.general_location && (
                            <a href={`https://www.google.com/maps/search/${encodeURIComponent(j.general_location)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs text-saddle hover:text-blaze transition-colors" data-testid="journal-location-pin">
                              <MapPin size={12} /> {j.general_location}
                            </a>
                          )}
                          {(j.weather || j.moon_phase) && (
                            <div className="flex flex-wrap gap-1.5 mt-2" data-testid="journal-conditions">
                              {j.entry_date && <span className="text-[10px] bg-bone px-2 py-1 text-charcoal/70">{j.entry_date}</span>}
                              {j.moon_phase && <span className="text-[10px] bg-forest text-bone px-2 py-1 inline-flex items-center gap-1"><Moon size={10} /> {j.moon_phase} · {j.moon_illumination}%</span>}
                              {j.weather && <span className="text-[10px] bg-[#e6efe9] text-[#234b38] px-2 py-1 inline-flex items-center gap-1"><CloudSun size={10} /> {j.weather_source === "observed" ? "Observed" : "Typical"}: {j.weather}</span>}
                            </div>
                          )}
                        </div>
                        <button onClick={() => removeJournal(j.id)} className="text-charcoal/40 hover:text-destructive transition-colors shrink-0" aria-label="Delete" data-testid="journal-delete"><Trash2 size={16} /></button>
                      </div>
                      {j.photos?.length > 0 && (
                        <div className="flex gap-2 mt-3">
                          {j.photos.map((p, i) => <img key={i} src={p} alt="" className="w-16 h-16 object-cover" data-testid="journal-photo-thumb" />)}
                        </div>
                      )}
                      <button
                        onClick={() => shareToFeed({ text: `${j.title}${j.body ? `\n\n${j.body}` : ""}`, media: j.photos?.[0] || null, media_type: j.photos?.[0] ? "image" : null, hunt_id: j.hunt_id || "" })}
                        className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-forest hover:text-blaze transition-colors"
                        data-testid="journal-share-feed"
                      >
                        <Send size={13} /> Share to Feed
                      </button>
                    </div>
                  ))}
                  {journals.length === 0 && <p className="text-charcoal/50 text-sm">No entries yet. Keep a record of every season — private or published.</p>}
                </>
              )}

              {tab === "Saved" && (
                saves.length ? saves.map((s) => (
                  <div key={s.id} className="bg-white border border-saddle/20 p-4 flex items-center gap-3" data-testid="profile-saved-item">
                    <span className={`hr-overline text-[9px] px-2 py-1 ${s.item_type === "hunt" ? "bg-[#e6efe9] text-[#234b38]" : "bg-[#f3ddca] text-[#7d451e]"}`}>{s.item_type}</span>
                    <p className="text-forest text-sm font-semibold">{s.notes || s.item_reference.slice(0, 16)}</p>
                  </div>
                )) : <p className="text-charcoal/50 text-sm">No saved items yet. Bookmark gear or a hunt to keep it here.</p>
              )}

              {tab === "Messages" && (
                myInquiries.length ? myInquiries.map((q) => (
                  <div key={q.id} className="bg-white border border-saddle/20 p-4" data-testid="thread-item">
                    <div className="flex items-center justify-between">
                      <span className="hr-overline text-saddle text-[9px]">{q.listing_title}</span>
                      <span className={`hr-overline text-[9px] ${q.status === "replied" ? "text-[#2f7d4f]" : "text-blaze"}`}>{q.status?.replace(/_/g, " ")}</span>
                    </div>
                    <Thread messages={q.messages} />
                    <div className="mt-2 grid grid-cols-[1fr_auto] gap-2">
                      <input value={threadDraft[q.id] || ""} onChange={(e) => setThreadDraft((d) => ({ ...d, [q.id]: e.target.value }))} placeholder="Reply to the provider…" className="border border-saddle/25 px-2 py-2 text-sm outline-none min-w-0" data-testid="thread-input" />
                      <button onClick={() => sendThreadMessage(q.id)} className="bg-forest text-bone px-3 grid place-items-center hover:bg-forest-2 transition-colors" data-testid="thread-send"><Send size={15} /></button>
                    </div>
                  </div>
                )) : <p className="text-charcoal/50 text-sm">No conversations yet. Send an inquiry from any hunt page to start a thread.</p>
              )}

              {tab === "Inbox" && (
                inquiries.length ? inquiries.map((q) => (
                  <div key={q.id} className="bg-white border border-saddle/20 p-4" data-testid="inbox-item">
                    <div className="flex items-center justify-between">
                      <span className="hr-overline text-saddle text-[9px] flex items-center gap-1"><Mail size={12} /> {q.listing_title}</span>
                      <span className={`hr-overline text-[9px] ${q.status === "replied" ? "text-[#2f7d4f]" : "text-blaze"}`}>{q.status?.replace(/_/g, " ")}</span>
                    </div>
                    <h4 className="text-forest font-semibold mt-1.5">{q.name} <span className="text-charcoal/50 font-normal text-sm">· {q.email}</span></h4>
                    <p className="text-xs text-charcoal/60 mt-0.5">{q.hunters} hunter(s){q.preferred_dates ? ` · ${q.preferred_dates}` : ""}</p>
                    <Thread messages={q.messages && q.messages.length ? q.messages : (q.message ? [{ sender: "hunter", name: q.name, text: q.message }] : [])} />
                    <div className="mt-2 grid grid-cols-[1fr_auto] gap-2">
                      <input value={replyDraft[q.id] || ""} onChange={(e) => setReplyDraft((d) => ({ ...d, [q.id]: e.target.value }))} placeholder="Reply to hunter…" className="border border-saddle/25 px-2 py-2 text-sm outline-none min-w-0" data-testid="inbox-reply-input" />
                      <button onClick={() => sendReply(q.id)} className="bg-forest text-bone px-3 grid place-items-center hover:bg-forest-2 transition-colors" data-testid="inbox-reply-send"><Send size={15} /></button>
                      <p className="col-span-2 text-[11px] text-charcoal/45 flex items-center gap-1"><Mail size={11} /> A copy is emailed to {q.email} so the conversation continues off-platform.</p>
                    </div>
                  </div>
                )) : <p className="text-charcoal/50 text-sm">No inquiries yet. Hunter inquiries from listing pages arrive here.</p>
              )}
            </div>

            <div className="p-6 pt-3 border-t border-saddle/20">
              <button onClick={() => { logout(); setProfileOpen(false); toast("Signed out"); }} className="w-full border border-saddle/30 py-3 font-semibold text-charcoal hover:bg-bone transition-colors flex items-center justify-center gap-2" data-testid="profile-logout"><LogOut size={16} /> Sign out</button>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
